import React, { useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import Dialog from "../../../components/Dialog";
import { StyledModalTitle } from "../../../styles/pages/Calendar/ModalInfoManagementStyles";
import ModalReportStyles from "../../../styles/pages/Summaries/ModalReportStyles";
import InputText from "../../../components/InputText";
import TableStyles from "../../../styles/components/TableStyles";
import PaginationLocal from "../../../components/PaginationLocal";
import ButtonStyles from "../../../styles/components/ButtonStyles";

import S from "../../../styles/pages/HistoryTach/ChoosePeriodModal";
import {
    getAllPeriods,
    postPrintAllDocuments,
} from "../../../service/Docente/HistoryTachService";
import { StyledFormCheck } from "../../../styles/MainStyles";
import { useTachHistory } from "../../../context/TachContext/TachHistoryContext";
import DialogMessageProps from "../../../model/DialogMessageProps";
import { TypeDialogMessageEnum } from "../../../model/Enums/TypeDialogMessageEnum";
import DialogMessage from "../../../components/DialogMessage";
import GenericUtils from "../../../utils/GenericUtils";
import AlertModal from "../AlertModal";
import { TypeTeacherOption } from "../../../utils/TachUtils";

const { StyledSearchInput } = ModalReportStyles;
const { BtnPrimary, BtnOutlinePrimary, DefaultButtonLink } = ButtonStyles;

type ChoosePeriodModalProps = {
    openModal: boolean;
    onHide: () => void;
    visibility?: boolean;
};

type Period = {
    id: number;
    vigencia: string;
    add: boolean;
};

const ChoosePeriodModal: React.FC<ChoosePeriodModalProps> = ({
    openModal,
    onHide,
    visibility,
}) => {
    const { instance } = useMsal();
    const [periods, setPeriods] = React.useState<Period[]>([]);
    const [filteredPeriods, setFilteredPeriods] = React.useState<Period[]>([]);
    const [searchedPeriods, setSearchedPeriods] = React.useState<Period[]>([]);
    const [search, setSearch] = React.useState<string>("");
    const { selectedTeacher } = useTachHistory();
    const linkRef = useRef<HTMLAnchorElement>(null);
    const [success, setSuccess] = React.useState<boolean>(false);
    const [showAlertModal, setShowAlertModal] = React.useState<boolean>(false);
    const [showDialog, setShowDialog] = React.useState(visibility ?? true);
    const [dialogMessageProps, setDialogMessageProps] =
        React.useState<DialogMessageProps>({} as DialogMessageProps);
    const handleCloseDialogMessage = () => {
        setDialogMessageProps({} as DialogMessageProps);
    };

    const getInitialData = async () => {
        const periodsResponse = await getAllPeriods(
            instance,
            (selectedTeacher[0] as any).value
        );

        if (GenericUtils.isSuccessStatus(periodsResponse)) {
            const periodsWithAdd = periodsResponse.data.vigenciaDocumentos.map(
                (period: any) => ({
                    ...period,
                    add: false,
                })
            );
            setPeriods(periodsWithAdd);
            setFilteredPeriods(periodsWithAdd);
            setSearchedPeriods(periodsWithAdd);
        }
    };

    const handleCheck = (id: number) => {
        const newPeriods = [...periods];
        const index = newPeriods.findIndex((period) => period.id === id);
        newPeriods[index].add = !newPeriods[index].add;

        setPeriods(newPeriods);
    };

    const handleCheckAll = () => {
        const periodsWithAdd = periods.map((period) => ({
            ...period,
            add: true,
        }));

        setPeriods(periodsWithAdd);
        setFilteredPeriods(periodsWithAdd);
        setSearchedPeriods(periodsWithAdd);
    };

    const handleSearch = (searchedText: string): void => {
        setSearch(searchedText);

        if (!searchedText.length) {
            setSearchedPeriods(periods);
            return;
        }

        const searchedPeriodsResult = periods.filter((period) =>
            period.vigencia.toLowerCase().includes(searchedText.toLowerCase())
        );

        setSearchedPeriods(searchedPeriodsResult);
    };

    const handleCloseAlertModal = () => {
        setShowAlertModal(false);
        setShowDialog(true);
    };

    const alert = (
        <AlertModal open={showAlertModal} onClose={handleCloseAlertModal} />
    );

    const getAllSelectedPeriods = () => {
        return periods.filter((period) => period.add);
    };

    const handlePrint = async () => {
        const selectedPeriods = getAllSelectedPeriods();

        if (selectedPeriods.length === 0) {
            setShowDialog(false);
            setShowAlertModal(true);
            return;
        }

        const teacherId = (selectedTeacher[0] as TypeTeacherOption)
            .value as unknown as number;

        const selectedPeriodsIds = selectedPeriods.map(
            (selectedPeriod) => selectedPeriod.id
        );

        const valuePeriodsIds = selectedPeriodsIds.join(",");

        const response = await postPrintAllDocuments(
            instance,
            teacherId,
            valuePeriodsIds
        );

        if (GenericUtils.isSuccessStatus(response)) {
            const { signedUrl } = response.data;

            linkRef.current?.setAttribute("href", signedUrl);
            linkRef.current?.click();

            setSuccess(true);
        }

        const dialog = {
            ...GenericUtils.getPropsDialogError(response),
            onClose: handleCloseDialogMessage,
        };
        setShowDialog(false);
        setDialogMessageProps(dialog);
    };

    const typeDialogMessage = (typeIcon: any) => {
        return success ? TypeDialogMessageEnum.Success : typeIcon;
    };

    const textMessage = (message: any) => {
        if (success) {
            return (
                <span style={{ fontSize: 24, fontWeight: 700 }}>Download</span>
            );
        }

        return message && message.length
            ? message
            : "Ocorreu um erro ao processar sua solicitação.";
    };

    const finishProcess = () => {
        onHide();
    };

    const dialogMessage = dialogMessageProps.show && (
        <DialogMessage
            type={typeDialogMessage(dialogMessageProps.type)}
            show={dialogMessageProps.show}
            title={dialogMessageProps.title}
            text={textMessage(dialogMessageProps.text)}
            onClose={finishProcess}
        />
    );

    useEffect(() => {
        getInitialData();
    }, []);

    const dialogClass = `${showDialog ? "visible-dialog" : "hidden-dialog"}`;

    return (
        <Dialog
            openModal={openModal}
            onHide={() => onHide()}
            className={dialogClass}
            size="width-600 modal-radius-8"
        >
            <StyledModalTitle>Selecione as vigências</StyledModalTitle>
            <StyledSearchInput>
                <InputText
                    value={search}
                    placeholder="Pesquisar por vigência"
                    onChange={(e) => handleSearch(e.target.value as string)}
                />
            </StyledSearchInput>
            <TableStyles.TablePrimary
                bordered
                striped
                responsive
                style={{ marginBottom: 0 }}
            >
                <thead>
                    <tr>
                        <th>Vigência</th>
                        <th>Adicionar</th>
                    </tr>
                </thead>
                <tbody>
                    {periods.length === 0 ? (
                        <tr>
                            <td colSpan={2}>Informações indisponíveis.</td>
                        </tr>
                    ) : (
                        filteredPeriods.map((period) => (
                            <tr key={period.id}>
                                <td>{period.vigencia}</td>
                                <td>
                                    <StyledFormCheck
                                        type="checkbox"
                                        id={period.id}
                                        checked={period.add}
                                        onChange={() => {
                                            handleCheck(period.id);
                                        }}
                                    />
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </TableStyles.TablePrimary>
            <PaginationLocal
                items={searchedPeriods}
                itemsPage={10}
                onPaginate={(filteredData) => setFilteredPeriods(filteredData)}
                hidePagination={false}
            />
            <S.ChoosePeriodModalButtonsContainer>
                <BtnOutlinePrimary onClick={() => handleCheckAll()}>
                    Adicionar Todos
                </BtnOutlinePrimary>
                <BtnPrimary onClick={() => handlePrint()}>
                    Imprimir selecionados
                </BtnPrimary>
            </S.ChoosePeriodModalButtonsContainer>
            <S.ChoosePeriodModalCancelButtonContainer>
                <DefaultButtonLink onClick={() => onHide()}>
                    Cancelar
                </DefaultButtonLink>
            </S.ChoosePeriodModalCancelButtonContainer>
            <a
                href="https://www.google.com"
                target="_blank"
                rel="noreferrer"
                ref={linkRef}
                style={{ display: "none" }}
            >
                attachments
            </a>
            {alert}
            {dialogMessage}
        </Dialog>
    );
};

export { ChoosePeriodModal };
